import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';

const route = 'ASSETS_IMPORT';

// Initial state
const initialState = {
  openImport: false,
  loading: false,
  importResponse: null,
  error: null,
  fileKey: null,
  delimiter: '',
  importNotification: null,
};

// Async thunk for importing assets
export const importAssets = createAsyncThunk(
  `${route}/importAssets`,
  async (payload, { rejectWithValue }) => {
    try {
      const { fileKey, delimiter } = payload;

      const query = `
        mutation importAssets ($fileKey: String!, $delimiter: String!) {
          importAssets (fileKey: $fileKey, delimiter: $delimiter) {
            stepFunctionId
            transactionId
            transactionType
            processStartDate
          }
        }
      `;

      const response = await clientMutation(
        query,
        {
          fileKey,
          delimiter,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (!response || !response.importAssets) {
        throw new Error('Error while importing assets', response.error);
      }

      return response.importAssets;
    } catch (exc) {
      ExceptionManager(
        exc,
        'modules/fleet/components/ModalImportAssets/redux/slice',
        'importAssets'
      );
      return rejectWithValue(exc.error);
    }
  }
);

// Slice
const assetsImportSlice = createSlice({
  name: route,
  initialState,
  reducers: {
    // Open/close modal actions
    openImportAssetsModal: (state, action) => {
      state.openImport = action.payload;
      if (!action.payload) {
        // Reset state when closing modal
        state.importResponse = null;
        state.error = null;
        state.fileKey = null;
        state.importNotification = null;
      }
    },
    
    // Set file key for import
    setFileKey: (state, action) => {
      state.fileKey = action.payload;
    },
    
    // Set delimiter
    setDelimiter: (state, action) => {
      state.delimiter = action.payload;
    },

    // Set import notification from websocket
    setImportNotificationAssets: (state, action) => {
      // Add validation for notification payload structure
      const notification = action.payload;
      if (notification && typeof notification === 'object') {
        state.importNotification = notification;
      } else {
        state.importNotification = null;
      }
    },
    
    // Reset import state
    resetImportState: (state) => {
      state.loading = false;
      state.importResponse = null;
      state.error = null;
      state.fileKey = null;
      state.delimiter = ',';
      state.importNotification = null;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Import assets pending
      .addCase(importAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.importResponse = null;
      })
      // Import assets fulfilled
      .addCase(importAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.importResponse = action.payload;
        state.error = null;
      })
      // Import assets rejected
      .addCase(importAssets.rejected, (state, action) => {
        state.loading = false;
        state.importResponse = null;
        state.error = action.payload || 'Import failed';
      });
  },
});

// Export actions
export const {
  openImportAssetsModal,
  setFileKey,
  setDelimiter,
  resetImportState,
  clearError,
  setImportNotificationAssets,
} = assetsImportSlice.actions;

// Export reducer
export default assetsImportSlice.reducer;